<template>
    <div>
        <CCardBody>
            <div class="text-right btngroup">
                <CButton class="addBtn" @click="showUploadModal">
                    Upload Custom Font
                </CButton>
            </div>
            <p class="text-muted font-md mb-0">
                Fonts uploaded here are managed by the admin and are shared with every customer of the
                companies the font is attached to. A font attached to all companies is available platform wide.
            </p>
            <!--
                Only a convenience filter: a platform admin sees every font on the platform and may
                want to narrow it down to one company. A merchant admin only ever gets their own
                company's fonts back, so there is nothing to filter and the control stays hidden.
                Nothing on this screen is gated on the filter - clearing it lists everything again.
            -->
            <b-form-group
                v-if="is_platform_admin"
                label="Filter by company"
                label-for="custom_font_company_filter"
                class="mt-3 mb-0"
                style="max-width: 420px"
            >
                <v-select
                    id="custom_font_company_filter"
                    v-model="company_filter"
                    :options="company_options"
                    :filterable="false"
                    label="company_name"
                    placeholder="All companies"
                    @search="onCompanySearch"
                >
                    <template #no-options>
                        <span class="text-muted">Type to search companies</span>
                    </template>
                </v-select>
                <small class="text-muted">
                    Clear the filter to list the fonts of every company again.
                </small>
            </b-form-group>
            <search-items
                v-model="search"
                :searchHandler="applySearch"
                placeholder="Search fonts by name"
            ></search-items>
            <b-table
                striped
                responsive="sm"
                show-empty
                id="customFontList"
                :items="filtered_fonts"
                :fields="headers"
                :busy="getCustomFontsLoading"
                empty-text="No custom fonts uploaded yet"
            >
                <template #cell(preview)="item">
                    <span class="font-preview" :style="previewStyle(item.item)">{{ sample_text }}</span>
                </template>
                <template #cell(display_name)="item">
                    <div v-if="rename.id === item.item.id" class="d-flex align-items-center">
                        <b-form-input
                            v-model="rename.display_name"
                            size="sm"
                            class="rename_input mr-2"
                            @keyup.enter="saveRename"
                            @keyup.esc="cancelRename"
                        ></b-form-input>
                        <b-button size="sm" variant="primary" class="mr-1" @click="saveRename">Save</b-button>
                        <b-button size="sm" @click="cancelRename">Cancel</b-button>
                    </div>
                    <div v-else>
                        <span>{{ item.item.display_name }}</span>
                        <br>
                        <small class="text-muted">{{ item.item.family_name }}</small>
                    </div>
                </template>
                <template #cell(format)="item">
                    <span>{{ formatLabel(item.item) }}</span>
                </template>
                <template #cell(file_size)="item">
                    <span>{{ formatFileSize(item.item.file_size) }}</span>
                </template>
                <template #cell(version)="item">
                    <span>v{{ item.item.version }}</span>
                </template>
                <template #cell(companies)="item">
                    <b-badge v-if="item.item.is_global" variant="success">All companies</b-badge>
                    <template v-else-if="fontCompanies(item.item).length > 0">
                        <b-badge
                            v-for="company in visibleFontCompanies(item.item)"
                            :key="company.id"
                            variant="info"
                            class="mr-1 mb-1"
                        >{{ company.company_name }}</b-badge>
                        <!-- the rest are only named in the tooltip, a font can carry dozens -->
                        <b-badge
                            v-if="hiddenFontCompanies(item.item).length > 0"
                            variant="secondary"
                            class="mb-1"
                            :title="hiddenFontCompanies(item.item).map(company => company.company_name).join(', ')"
                        >+{{ hiddenFontCompanies(item.item).length }} more</b-badge>
                    </template>
                    <span v-else class="text-muted">Not attached</span>
                </template>
                <template #cell(usage_count)="item">
                    <span>{{ item.item.usage_count }}</span>
                </template>
                <template #cell(created_at)="item">
                    <span v-if="item.item.created_at">{{ item.item.created_at | moment("DD MMM YYYY HH:mm") }}</span>
                </template>
                <template #cell(actions)="item">
                    <edit
                        class="font_action ml-2"
                        title="Rename"
                        fillColor="#009FE3"
                        @click="startRename(item.item)"
                    />
                    <upload
                        class="font_action ml-2"
                        title="Replace file"
                        fillColor="#009FE3"
                        @click="showReplaceModal(item.item)"
                    />
                    <!-- a merchant admin has no other company to attach, so the action is theirs only -->
                    <domain
                        v-if="is_platform_admin"
                        class="font_action ml-2"
                        title="Edit companies"
                        fillColor="#009FE3"
                        @click="showCompaniesModal(item.item)"
                    />
                    <delete
                        class="font_action ml-2"
                        title="Delete"
                        fillColor="#E30000"
                        @click="showDeleteModal(item.item)"
                    />
                </template>
            </b-table>
        </CCardBody>

        <!-- upload modal, the api takes one file per request so the queue is uploaded sequentially -->
        <b-modal id="customFontUploadModal" size="lg" no-close-on-backdrop hide-footer @hide="resetUploadForm">
            <template #modal-title>
                <span>Upload Custom Font</span>
            </template>
            <b-form-group label="Font files" label-for="custom_font_files">
                <b-form-file
                    id="custom_font_files"
                    ref="custom-font-files"
                    multiple
                    :accept="accepted_extensions_attr"
                    placeholder="Click or drag to insert font files"
                    drop-placeholder="Drop font files here"
                    @input="addFilesToQueue"
                ></b-form-file>
                <small class="text-muted">
                    Allowed formats: {{ allowed_extensions.join(', ') }}. Maximum size: {{ formatFileSize(max_file_size) }} per file.
                </small>
            </b-form-group>

            <!--
                Every file in the queue is attached to the same companies - the queue exists only
                because the endpoint takes one file per request, not because the files differ.
                Hidden for a merchant admin: the API attaches the font to their own company.
            -->
            <template v-if="is_platform_admin">
                <b-form-group class="mb-2">
                    <b-form-checkbox v-model="upload_is_global">
                        All companies
                    </b-form-checkbox>
                    <small class="text-muted">
                        Makes the font available to the customers of every company on the platform.
                    </small>
                </b-form-group>
                <b-form-group
                    v-if="!upload_is_global"
                    label="Companies"
                    label-for="custom_font_upload_companies"
                >
                    <v-select
                        id="custom_font_upload_companies"
                        v-model="upload_companies"
                        :options="company_options"
                        :filterable="false"
                        multiple
                        label="company_name"
                        placeholder="Search and select one or more companies"
                        @search="onCompanySearch"
                    >
                        <template #no-options>
                            <span class="text-muted">Type to search companies</span>
                        </template>
                    </v-select>
                    <small class="text-muted">
                        The font is shared with every customer of the selected companies.
                    </small>
                </b-form-group>
            </template>

            <div v-if="upload_errors.length > 0">
                <b-alert v-for="(err, index) in upload_errors" variant="danger" :key="index" show>
                    {{ err }}
                </b-alert>
            </div>

            <b-table
                v-if="upload_queue.length > 0"
                striped
                responsive="sm"
                :items="upload_queue"
                :fields="queue_headers"
            >
                <template #cell(file_name)="item">
                    <span>{{ item.item.file.name }}</span>
                    <br>
                    <small class="text-muted">{{ formatFileSize(item.item.file.size) }}</small>
                </template>
                <template #cell(display_name)="item">
                    <b-form-input
                        v-model="item.item.display_name"
                        size="sm"
                        :disabled="item.item.status === 'uploading' || item.item.status === 'done'"
                        placeholder="Optional display name"
                    ></b-form-input>
                </template>
                <template #cell(status)="item">
                    <b-progress
                        v-if="item.item.status === 'uploading'"
                        :value="item.item.progress"
                        :max="100"
                        show-progress
                    ></b-progress>
                    <span v-else-if="item.item.status === 'done'" class="text-success">Uploaded</span>
                    <span v-else-if="item.item.status === 'error'" class="error_msg">{{ item.item.error }}</span>
                    <span v-else class="text-muted">Ready</span>
                </template>
                <template #cell(actions)="item">
                    <delete
                        v-if="item.item.status !== 'uploading'"
                        class="font_action"
                        title="Remove from queue"
                        fillColor="#E30000"
                        @click="removeFromQueue(item.index)"
                    />
                </template>
            </b-table>

            <hr>
            <div class="text-right mt-2">
                <b-button
                    variant="primary"
                    :disabled="upload_in_progress || pending_uploads === 0"
                    @click="startUpload"
                >
                    Upload
                </b-button>
            </div>
        </b-modal>

        <!-- replace file modal -->
        <b-modal id="customFontReplaceModal" size="md" no-close-on-backdrop hide-footer @hide="resetReplaceForm">
            <template #modal-title>
                <span>Replace Font File</span>
            </template>
            <p class="font-md">
                Replacing the file of <strong>{{ replace.display_name }}</strong> keeps the same font family, every
                design already using it will pick up the new file.
            </p>
            <b-form-group label="Font file" label-for="custom_font_replace_file">
                <b-form-file
                    id="custom_font_replace_file"
                    v-model="replace.file"
                    :accept="accepted_extensions_attr"
                    placeholder="Click or drag to insert a font file"
                ></b-form-file>
                <small class="text-muted">
                    Allowed formats: {{ allowed_extensions.join(', ') }}. Maximum size: {{ formatFileSize(max_file_size) }}.
                </small>
            </b-form-group>
            <b-progress
                v-if="replace.status === 'uploading'"
                :value="replace.progress"
                :max="100"
                show-progress
                class="mb-2"
            ></b-progress>
            <div v-if="replace.error" class="error_msg">{{ replace.error }}</div>
            <hr>
            <div class="text-right mt-2">
                <b-button
                    variant="primary"
                    :disabled="replace.status === 'uploading'"
                    @click="saveReplace"
                >
                    Replace File
                </b-button>
            </div>
        </b-modal>

        <!-- attachment editor: the same font can be handed to any number of companies -->
        <b-modal id="customFontCompaniesModal" size="lg" no-close-on-backdrop @hide="resetCompaniesForm">
            <template #modal-title>
                <span>Edit Companies</span>
            </template>
            <p class="font-md">
                <strong>{{ companies_edit.display_name }}</strong> is shared with every customer of the
                companies attached below.
            </p>
            <b-form-group class="mb-2">
                <b-form-checkbox v-model="companies_edit.is_global">
                    All companies
                </b-form-checkbox>
                <small class="text-muted">
                    Makes the font available to the customers of every company on the platform.
                </small>
            </b-form-group>
            <b-form-group
                v-if="!companies_edit.is_global"
                label="Companies"
                label-for="custom_font_edit_companies"
                class="mb-2"
            >
                <v-select
                    id="custom_font_edit_companies"
                    v-model="companies_edit.companies"
                    :options="company_options"
                    :filterable="false"
                    multiple
                    label="company_name"
                    placeholder="Search and select one or more companies"
                    @search="onCompanySearch"
                >
                    <template #no-options>
                        <span class="text-muted">Type to search companies</span>
                    </template>
                </v-select>
            </b-form-group>
            <!--
                409 from the sync endpoint: a company that would lose the font still has work on it.
                The counts are shown before anything is written, and only then is force offered.
            -->
            <b-alert v-if="companies_edit.conflicts.length > 0" variant="warning" show class="mt-2">
                <strong>These companies still use {{ companies_edit.display_name }}:</strong>
                <ul class="mb-2 mt-1 pl-4">
                    <li v-for="conflict in companies_edit.conflicts" :key="conflict.company_id">
                        {{ conflict.company_name }} &mdash; {{ usageLine(conflict.usage) }}
                    </li>
                </ul>
                Removing them switches those saved designs and active carts to a default font.
                Placed orders are never modified &mdash; they keep this font.
            </b-alert>
            <div v-if="companies_edit.error" class="error_msg">{{ companies_edit.error }}</div>
            <template #modal-footer="{ hide }">
                <b-button
                    v-if="companies_edit.conflicts.length > 0"
                    size="md"
                    variant="danger"
                    :disabled="companies_edit.saving"
                    @click="saveCompanies(true)"
                >
                    Remove anyway
                </b-button>
                <b-button
                    v-else
                    size="md"
                    variant="primary"
                    :disabled="companies_edit.saving"
                    @click="saveCompanies(false)"
                >
                    Save Changes
                </b-button>
                <b-button size="md" @click="hide()">Cancel</b-button>
            </template>
        </b-modal>

        <!-- delete modal -->
        <b-modal id="customFontDeleteModal" size="md" no-close-on-backdrop @hide="resetDeleteForm">
            <template #modal-title>
                <span>Delete Custom Font</span>
            </template>
            <p class="font-md">
                <strong>Warning:</strong> deleting <strong>{{ remove.display_name }}</strong> removes it from every
                company it is attached to.
            </p>
            <!-- the usage lookup runs while the dialog is already open, it is a second request -->
            <div v-if="remove.usage_loading" class="font-md text-muted">
                Checking which products use this font&hellip;
            </div>
            <template v-else-if="remove.usage">
                <template v-if="remove.usage.total > 0">
                    <p class="font-md mb-1">
                        It is currently used by {{ usageLine(remove.usage) }}.
                    </p>
                    <ul v-if="remove.usage.companies && remove.usage.companies.length > 0" class="font-md pl-4">
                        <li v-for="entry in remove.usage.companies" :key="entry.company_id">
                            {{ entry.company_name }} &mdash; {{ usageLine(entry) }}
                        </li>
                    </ul>
                    <p class="font-md">
                        Saved designs and active carts switch to a default font. Placed orders are never
                        modified &mdash; they keep this font, so existing orders still render as they were placed.
                    </p>
                </template>
                <p v-else class="font-md">Nothing is using it at the moment.</p>
            </template>
            <!-- the counts above can go stale between the lookup and the delete, so 409 is still handled -->
            <b-alert v-if="remove.conflicts.length > 0" variant="warning" show>
                <strong>{{ remove.display_name }} is still in use:</strong>
                <ul class="mb-2 mt-1 pl-4">
                    <li v-for="conflict in remove.conflicts" :key="conflict.company_id">
                        {{ conflict.company_name }} &mdash; {{ usageLine(conflict.usage) }}
                    </li>
                </ul>
                Deleting it switches those saved designs and active carts to a default font.
                Placed orders are never modified &mdash; they keep this font.
            </b-alert>
            <div v-if="remove.error" class="error_msg">{{ remove.error }}</div>
            <template #modal-footer="{ hide }">
                <b-button
                    size="md"
                    variant="danger"
                    :disabled="remove.usage_loading || remove.deleting"
                    @click="confirmDelete(remove.conflicts.length > 0)"
                >
                    {{ remove.conflicts.length > 0 ? 'Delete anyway' : 'Delete' }}
                </b-button>
                <b-button size="md" @click="hide()">Cancel</b-button>
            </template>
        </b-modal>
    </div>
</template>
<script>
import Delete from 'vue-material-design-icons/Delete.vue';
import Edit from 'vue-material-design-icons/Pencil.vue';
import Upload from 'vue-material-design-icons/Upload.vue';
import Domain from 'vue-material-design-icons/Domain.vue';
import SearchItems from '@/components/SearchItems.vue';
import ErrorMessages from '@/mixins/ErrorMessages';
import { mapActions, mapGetters } from 'vuex';
// The company lookup is a one-off read that does not belong in the fonts store module.
import { http } from '@/config/httpCommon';

const PREVIEW_STYLE_ELEMENT_ID = 'custom-fonts-preview-font-faces';
const FONT_FACE_FORMATS = {
    ttf: 'truetype',
    otf: 'opentype',
    woff: 'woff',
    woff2: 'woff2',
};

export default {
    name: 'Listing',
    mixins: [ErrorMessages],
    components: {
        Delete,
        Edit,
        Upload,
        Domain,
        SearchItems,
    },
    data() {
        return {
            search: '',
            sample_text: 'AaBbCcDd 12345',
            company_filter: null,
            company_options: [],
            company_search_timer: null,
            storage_url: process.env.VUE_APP_FILE_URL,
            allowed_extensions: ['ttf', 'otf', 'woff', 'woff2'],
            max_file_size: 5 * 1024 * 1024,
            headers: [
                { key: 'preview', label: 'Preview' },
                { key: 'display_name', label: 'Name' },
                { key: 'format', label: 'Format' },
                { key: 'file_size', label: 'Size' },
                { key: 'version', label: 'Version' },
                { key: 'companies', label: 'Companies' },
                { key: 'usage_count', label: 'Used by' },
                { key: 'created_at', label: 'Date upload' },
                { key: 'actions', label: 'Actions' },
            ],
            queue_headers: [
                { key: 'file_name', label: 'File' },
                { key: 'display_name', label: 'Display name' },
                { key: 'status', label: 'Status' },
                { key: 'actions', label: '' },
            ],
            upload_queue: [],
            upload_errors: [],
            upload_in_progress: false,
            // one attachment set for the whole queue, see the upload modal comment
            upload_is_global: false,
            upload_companies: [],
            max_visible_companies: 3,
            companies_edit: {
                id: null,
                display_name: '',
                is_global: false,
                companies: [],
                conflicts: [],
                error: null,
                saving: false,
            },
            rename: {
                id: null,
                display_name: '',
            },
            replace: {
                id: null,
                display_name: '',
                file: null,
                progress: 0,
                status: null,
                error: null,
            },
            remove: {
                id: null,
                display_name: '',
                usage: null,
                usage_loading: false,
                conflicts: [],
                error: null,
                deleting: false,
            },
        }
    },
    computed: {
        ...mapGetters(['getCustomFonts', 'getCustomFontsLoading']),
        /*
        * A merchant admin carries their own company_id and the API scopes every call to it, so
        * there is nothing for them to filter and nothing to attach - the API uses their own
        * company. A platform admin (SuperAdmin) has no company_id, sees every font, and is the
        * only one who picks the companies a font is attached to.
        * */
        is_platform_admin() {
            return !this.current_user_company_id;
        },
        current_user_company_id() {
            try {
                const user = JSON.parse(localStorage.getItem('user'));
                return user && user.company_id ? user.company_id : null;
            } catch (error) {
                return null;
            }
        },
        /* Listing filter only; null means "every font". Merchant admins never set one. */
        filter_company_id() {
            if (this.current_user_company_id) {
                return null;
            }
            return this.company_filter ? this.company_filter.id : null;
        },
        filtered_fonts() {
            const term = this.search ? this.search.toLowerCase().trim() : '';
            if (!term) {
                return this.getCustomFonts;
            }
            return this.getCustomFonts.filter(font => {
                const display_name = font.display_name ? font.display_name.toLowerCase() : '';
                const family_name = font.family_name ? font.family_name.toLowerCase() : '';
                const original_filename = font.original_filename ? font.original_filename.toLowerCase() : '';
                return display_name.includes(term) || family_name.includes(term) || original_filename.includes(term);
            });
        },
        accepted_extensions_attr() {
            return this.allowed_extensions.map(extension => `.${extension}`).join(',');
        },
        pending_uploads() {
            return this.upload_queue.filter(queued => queued.status !== 'done').length;
        },
    },
    watch: {
        company_filter() {
            this.upload_queue = [];
            this.upload_errors = [];
            this.loadCustomFonts();
        },
        getCustomFonts: {
            deep: true,
            handler() {
                this.injectFontFaces();
            }
        },
        /* A conflict list belongs to one exact attachment set - once the admin changes the
           selection the "Remove anyway" it justified no longer applies. */
        'companies_edit.is_global'() {
            this.companies_edit.conflicts = [];
        },
        'companies_edit.companies'() {
            this.companies_edit.conflicts = [];
        }
    },
    async created() {
        await this.loadCustomFonts();
    },
    beforeDestroy() {
        const style_element = document.getElementById(PREVIEW_STYLE_ELEMENT_ID);
        if (style_element && style_element.parentNode) {
            style_element.parentNode.removeChild(style_element);
        }
    },
    methods: {
        // the action is aliased because the `getCustomFonts` getter is already mapped above
        ...mapActions({
            fetchCustomFonts: 'getCustomFonts',
            uploadCustomFont: 'uploadCustomFont',
            renameCustomFont: 'renameCustomFont',
            replaceCustomFontFile: 'replaceCustomFontFile',
            syncCustomFontCompanies: 'syncCustomFontCompanies',
            fetchCustomFontUsage: 'fetchCustomFontUsage',
            deleteCustomFont: 'deleteCustomFont',
        }),
        async loadCustomFonts() {
            this.$store.commit('set', ['showLoader', true]);
            try {
                await this.fetchCustomFonts({ company_id: this.filter_company_id });
                this.injectFontFaces();
            } catch (err) {
                this.showError(err);
            }
            this.$store.commit('set', ['showLoader', false]);
        },
        applySearch() {
            // the listing endpoint returns the whole collection, name filtering is local
        },

        /* ---------- live preview ---------- */
        fontFamilyName(font) {
            // must stay identical to the family the builder registers for a custom font
            return `customfont-${font.id}`;
        },
        fontFileUrl(font) {
            // `path` is a storage key, the storage base is prepended exactly like the
            // other asset screens do (datasource / cuttings listings).
            const key = String(font.path).replace(/^\/+/, '');
            const cache_buster = font.version ? `?v=${font.version}` : '';
            return `${this.storage_url}/${key}${cache_buster}`;
        },
        fontFaceFormat(extension) {
            const key = extension ? String(extension).toLowerCase().replace('.', '') : '';
            return FONT_FACE_FORMATS[key] ? FONT_FACE_FORMATS[key] : 'truetype';
        },
        injectFontFaces() {
            let style_element = document.getElementById(PREVIEW_STYLE_ELEMENT_ID);
            if (!style_element) {
                style_element = document.createElement('style');
                style_element.id = PREVIEW_STYLE_ELEMENT_ID;
                style_element.setAttribute('type', 'text/css');
                document.head.appendChild(style_element);
            }
            const rules = [];
            this.getCustomFonts.forEach(font => {
                if (!font.path) {
                    return;
                }
                rules.push(
                    `@font-face { font-family: '${this.fontFamilyName(font)}';` +
                    ` src: url('${this.fontFileUrl(font)}') format('${this.fontFaceFormat(font.extension)}');` +
                    ` font-display: swap; }`
                );
            });
            style_element.textContent = rules.join('\n');
        },
        previewStyle(font) {
            return {
                fontFamily: `'${this.fontFamilyName(font)}', sans-serif`,
            };
        },

        /* ---------- display helpers ---------- */
        formatLabel(font) {
            return font.extension ? String(font.extension).toUpperCase().replace('.', '') : '';
        },
        fontCompanies(font) {
            // the api sends an empty array for a global font, older rows may send nothing at all
            return Array.isArray(font.companies) ? font.companies : [];
        },
        visibleFontCompanies(font) {
            return this.fontCompanies(font).slice(0, this.max_visible_companies);
        },
        hiddenFontCompanies(font) {
            return this.fontCompanies(font).slice(this.max_visible_companies);
        },
        /* One line of usage counts, shared by the detach and the delete dialogs. */
        usageLine(usage) {
            const designs = usage && usage.designs ? Number(usage.designs) : 0;
            const carts = usage && usage.carts ? Number(usage.carts) : 0;
            const orders = usage && usage.orders ? Number(usage.orders) : 0;
            return `${designs} saved design(s), ${carts} active cart(s), ${orders} placed order(s)`;
        },
        /*
        * Both destructive endpoints answer 409 with result.conflicts when the font is still in
        * use and force was not set. Anything else is a plain error and keeps the normal path.
        * */
        conflictsFromError(err) {
            if (!err || !err.response || err.response.status !== 409) {
                return null;
            }
            const result = err.response.data ? err.response.data.result : null;
            const conflicts = result ? result.conflicts : null;
            return Array.isArray(conflicts) ? conflicts : [];
        },
        formatFileSize(bytes) {
            const size = Number(bytes);
            if (!size || size <= 0) {
                return '0 KB';
            }
            if (size < 1024) {
                return `${size} B`;
            }
            if (size < 1024 * 1024) {
                return `${(size / 1024).toFixed(1)} KB`;
            }
            return `${(size / (1024 * 1024)).toFixed(2)} MB`;
        },
        extractErrorMessage(err) {
            const data = err && err.response ? err.response.data : null;
            const field_errors = data ? data.errors : null;
            if (field_errors && typeof field_errors === 'object' && !Array.isArray(field_errors)) {
                const first_field = Object.keys(field_errors)[0];
                if (first_field) {
                    const messages = field_errors[first_field];
                    return Array.isArray(messages) ? messages[0] : String(messages);
                }
            }
            if (data && data.message) {
                return String(data.message);
            }
            return err && err.message ? err.message : 'Something went wrong';
        },

        /* ---------- client side validation, mirrors the backend rules ---------- */
        fileExtension(file) {
            const name = file && file.name ? file.name : '';
            const dot_index = name.lastIndexOf('.');
            return dot_index > -1 ? name.substring(dot_index + 1).toLowerCase() : '';
        },
        validateFile(file) {
            if (!file) {
                return 'A font file is required';
            }
            const extension = this.fileExtension(file);
            if (!this.allowed_extensions.includes(extension)) {
                return `Unsupported file type "${extension ? extension : file.name}". Allowed formats: ${this.allowed_extensions.join(', ')}.`;
            }
            if (file.size > this.max_file_size) {
                return `File is ${this.formatFileSize(file.size)}, the maximum allowed size is ${this.formatFileSize(this.max_file_size)}.`;
            }
            return null;
        },

        /* ---------- company lookup ---------- */
        /* Debounced company lookup, shared by the filter, the upload modal and the attachment
           editor: /companies is paginated 10 at a time over ~274 rows, so the list is searched
           server side rather than pulled down and filtered locally. */
        onCompanySearch(term, loading) {
            if (!term || term.length < 2) {
                return;
            }
            if (this.company_search_timer) {
                clearTimeout(this.company_search_timer);
            }
            if (loading) loading(true);
            this.company_search_timer = setTimeout(async () => {
                try {
                    const res = await http.get('companies', { params: { search: term } });
                    const companies = res.data.result.companies;
                    this.company_options = companies && companies.data ? companies.data : (companies || []);
                } catch (err) {
                    this.company_options = [];
                } finally {
                    if (loading) loading(false);
                }
            }, 350);
        },
        /* ---------- upload ---------- */
        showUploadModal() {
            /* @hide resets the form, so the prefill has to happen on the way in: if the listing is
               filtered to one company that is almost certainly the company being uploaded for. */
            if (this.is_platform_admin && this.company_filter) {
                this.upload_companies = [this.company_filter];
            }
            this.$bvModal.show('customFontUploadModal');
        },
        addFilesToQueue(files) {
            const selected_files = Array.isArray(files) ? files : (files ? [files] : []);
            selected_files.forEach(file => {
                const already_queued = this.upload_queue.some(queued => {
                    return queued.file.name === file.name && queued.file.size === file.size;
                });
                if (already_queued) {
                    return;
                }
                const validation_error = this.validateFile(file);
                this.upload_queue.push({
                    file: file,
                    display_name: file.name.replace(/\.[^/.]+$/, ''),
                    progress: 0,
                    status: validation_error ? 'error' : 'ready',
                    error: validation_error,
                });
            });
        },
        removeFromQueue(index) {
            this.upload_queue.splice(index, 1);
        },
        async startUpload() {
            this.upload_errors = [];
            const uploadable = this.upload_queue.filter(queued => queued.status !== 'done');
            if (uploadable.length === 0) {
                this.upload_errors.push('Select at least one font file to upload');
                return;
            }
            // a font nobody can see is never what was meant, so the attachment is required
            if (this.is_platform_admin && !this.upload_is_global && this.upload_companies.length === 0) {
                this.upload_errors.push('Select at least one company, or tick "All companies"');
                return;
            }
            this.upload_in_progress = true;
            let uploaded_count = 0;
            // one file per request, so the queue is uploaded sequentially
            for (const queued of uploadable) {
                const validation_error = this.validateFile(queued.file);
                if (validation_error) {
                    queued.status = 'error';
                    queued.error = validation_error;
                    continue;
                }
                queued.status = 'uploading';
                queued.progress = 0;
                queued.error = null;
                try {
                    await this.uploadCustomFont({
                        file: queued.file,
                        display_name: queued.display_name,
                        // omitted for a merchant admin: the API attaches it to their own company
                        is_global: this.is_platform_admin ? this.upload_is_global : undefined,
                        company_ids: this.is_platform_admin && !this.upload_is_global
                            ? this.upload_companies.map(company => company.id)
                            : undefined,
                        onProgress: (percent) => {
                            queued.progress = percent;
                        }
                    });
                    queued.status = 'done';
                    queued.progress = 100;
                    ++uploaded_count;
                } catch (err) {
                    queued.status = 'error';
                    queued.error = this.extractErrorMessage(err);
                }
            }
            this.upload_in_progress = false;
            this.injectFontFaces();
            const failed_count = this.upload_queue.filter(queued => queued.status === 'error').length;
            if (uploaded_count > 0) {
                this.showToast(`${uploaded_count} custom font(s) uploaded successfully`, 'SUCCESS');
            }
            if (failed_count === 0) {
                this.$bvModal.hide('customFontUploadModal');
            } else {
                this.upload_errors.push(`${failed_count} file(s) could not be uploaded, see the list below.`);
            }
        },
        resetUploadForm() {
            this.upload_queue = [];
            this.upload_errors = [];
            this.upload_in_progress = false;
            this.upload_is_global = false;
            this.upload_companies = [];
            if (this.$refs['custom-font-files']) {
                this.$refs['custom-font-files'].reset();
            }
        },

        /* ---------- inline rename ---------- */
        startRename(font) {
            this.rename.id = font.id;
            this.rename.display_name = font.display_name;
        },
        cancelRename() {
            this.rename.id = null;
            this.rename.display_name = '';
        },
        async saveRename() {
            const display_name = this.rename.display_name ? this.rename.display_name.trim() : '';
            if (!display_name) {
                this.showError('Display name is required');
                return;
            }
            this.$store.commit('set', ['showLoader', true]);
            try {
                await this.renameCustomFont({
                    id: this.rename.id,
                    display_name: display_name,
                });
                this.cancelRename();
                this.showToast('Custom font renamed successfully', 'SUCCESS');
            } catch (err) {
                this.showError(err);
            }
            this.$store.commit('set', ['showLoader', false]);
        },

        /* ---------- replace file ---------- */
        showReplaceModal(font) {
            this.replace.id = font.id;
            this.replace.display_name = font.display_name;
            this.replace.file = null;
            this.replace.progress = 0;
            this.replace.status = null;
            this.replace.error = null;
            this.$bvModal.show('customFontReplaceModal');
        },
        async saveReplace() {
            const validation_error = this.validateFile(this.replace.file);
            if (validation_error) {
                this.replace.error = validation_error;
                return;
            }
            this.replace.error = null;
            this.replace.status = 'uploading';
            this.replace.progress = 0;
            try {
                await this.replaceCustomFontFile({
                    id: this.replace.id,
                    file: this.replace.file,
                    onProgress: (percent) => {
                        this.replace.progress = percent;
                    }
                });
                this.replace.status = 'done';
                this.injectFontFaces();
                this.$bvModal.hide('customFontReplaceModal');
                this.showToast('Font file replaced successfully', 'SUCCESS');
            } catch (err) {
                this.replace.status = 'error';
                this.replace.error = this.extractErrorMessage(err);
            }
        },
        resetReplaceForm() {
            this.replace = {
                id: null,
                display_name: '',
                file: null,
                progress: 0,
                status: null,
                error: null,
            };
        },

        /* ---------- attachments ---------- */
        showCompaniesModal(font) {
            this.companies_edit = {
                id: font.id,
                display_name: font.display_name,
                is_global: !!font.is_global,
                /* copied out of the row: v-select mutates the array it is bound to, and the
                   listing must not change until the sync actually went through. */
                companies: this.fontCompanies(font).map(company => {
                    return { id: company.id, company_name: company.company_name };
                }),
                conflicts: [],
                error: null,
                saving: false,
            };
            this.$bvModal.show('customFontCompaniesModal');
        },
        async saveCompanies(force) {
            const is_global = !!this.companies_edit.is_global;
            const company_ids = is_global ? [] : this.companies_edit.companies.map(company => company.id);
            if (!is_global && company_ids.length === 0) {
                this.companies_edit.error = 'Select at least one company, or tick "All companies"';
                return;
            }
            this.companies_edit.error = null;
            this.companies_edit.saving = true;
            try {
                await this.syncCustomFontCompanies({
                    id: this.companies_edit.id,
                    is_global: is_global,
                    company_ids: company_ids,
                    force: !!force,
                });
                this.$bvModal.hide('customFontCompaniesModal');
                this.showToast('Font companies updated successfully', 'SUCCESS');
            } catch (err) {
                const conflicts = this.conflictsFromError(err);
                if (conflicts) {
                    // nothing was written: the affected products are listed and force is offered
                    this.companies_edit.conflicts = conflicts;
                } else {
                    this.companies_edit.error = this.extractErrorMessage(err);
                }
            }
            this.companies_edit.saving = false;
        },
        resetCompaniesForm() {
            this.companies_edit = {
                id: null,
                display_name: '',
                is_global: false,
                companies: [],
                conflicts: [],
                error: null,
                saving: false,
            };
        },

        /* ---------- delete ---------- */
        async showDeleteModal(font) {
            this.remove = {
                id: font.id,
                display_name: font.display_name,
                usage: null,
                usage_loading: true,
                conflicts: [],
                error: null,
                deleting: false,
            };
            this.$bvModal.show('customFontDeleteModal');
            /* The listing only carries a total; the dialog has to name what breaks, so the
               breakdown is fetched with the dialog already open instead of delaying it. */
            try {
                const usage = await this.fetchCustomFontUsage({ id: font.id });
                if (this.remove.id === font.id) {
                    this.remove.usage = usage;
                }
            } catch (err) {
                if (this.remove.id === font.id) {
                    this.remove.error = this.extractErrorMessage(err);
                }
            }
            if (this.remove.id === font.id) {
                this.remove.usage_loading = false;
            }
        },
        async confirmDelete(force) {
            this.remove.error = null;
            this.remove.deleting = true;
            this.$store.commit('set', ['showLoader', true]);
            try {
                const res = await this.deleteCustomFont({ id: this.remove.id, force: !!force });
                this.$bvModal.hide('customFontDeleteModal');
                this.injectFontFaces();
                this.showToast(this.deletedMessage(res), 'SUCCESS');
            } catch (err) {
                const conflicts = this.conflictsFromError(err);
                if (conflicts) {
                    // the counts fetched above can go stale, so the 409 is shown the same way
                    this.remove.conflicts = conflicts;
                } else {
                    this.remove.error = this.extractErrorMessage(err);
                }
            }
            this.remove.deleting = false;
            this.$store.commit('set', ['showLoader', false]);
        },
        /* A forced delete reports what it moved onto the default font; a clean one has nothing. */
        deletedMessage(res) {
            const result = res && res.data ? res.data.result : null;
            const substituted = result ? result.substituted : null;
            const designs = substituted && substituted.designs ? Number(substituted.designs) : 0;
            const carts = substituted && substituted.carts ? Number(substituted.carts) : 0;
            if (designs > 0 || carts > 0) {
                return `Custom font deleted successfully, ${designs} design(s) and ${carts} cart(s)`
                    + ' switched to a default font';
            }
            return 'Custom font deleted successfully';
        },
        resetDeleteForm() {
            this.remove = {
                id: null,
                display_name: '',
                usage: null,
                usage_loading: false,
                conflicts: [],
                error: null,
                deleting: false,
            };
        },
    },
}
</script>
<style scoped type="scss">
    .font_action {
        cursor: pointer;
    }
    .font-preview {
        font-size: 22px;
        line-height: 1.4;
        white-space: nowrap;
    }
    .rename_input {
        max-width: 220px;
    }
</style>
